import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import ArthaMultiSelect from "../../fields/ArthaMultiSelect";
import ArthaCancelButton from "../../fields/ArthaCancelButton";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { handleDatePickerRawChange, dateFormatMaxLength } from "../../fields/datePickerInput";
import AddMember from "./AddMember";
import { ReactComponent as UserImage } from "../../Assets/mobileUser.svg";
import ArthaContinueButton from "../../fields/ArthaContinueButton";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable';
import "react-datepicker/dist/react-datepicker.css";
import Checkbox from '@mui/material/Checkbox';
import { orange } from '@mui/material/colors';
import "./dashboard.css";
import FormControlLabel from '@mui/material/FormControlLabel';
import {
  fetchMemberApi,
  generateApplicationUsageReport,
  fetchOrganizationUsers,
} from "../../actioncreators/actioncreators";
import { useSuspension } from "../../contexts/SuspensionContext";
import {
  Search,
  Settings,
  Download,
  ChevronDown,
  ChevronUp,
  Bell,
  Smartphone,
  CheckCircle,
  XCircle,
} from "lucide-react";

const formatTimestamp = (timestamp) => {
  const date = new Date(parseInt(timestamp));
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
};

const AppUsageRow = ({ rowData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);

  const getStatusIcon = (status) => {
    return status === 'Authorised' ? (
      <CheckCircle size={16} className="text-success" />
    ) : (
      <XCircle size={16} className="text-danger" />
    );
  };

  return (
    <>
      <tr 
        className="border-bottom border-top table-hover "
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: 'pointer' }}
      >
        <td className="px-3 py-3 text-secondary">
          {new Date(rowData.date).toLocaleDateString("en-GB")}
        </td>
        <td className="px-3 py-3 text-secondary">{rowData.totalScreenTime}</td>
        <td className="px-3 py-3 text-secondary">{rowData.authorisedAppUsageTime}</td>
        <td className="px-3 py-3 text-secondary">{rowData.unauthorisedAppUsageTime}</td>
        <td className="px-3 py-3 text-secondary">{rowData.uncategorisedAppUsageTime}</td>
        <td className="px-3 py-3 text-secondary">{rowData.screenUnlocks}</td>
        <td className="px-3 py-3 text-secondary">{rowData.notificationsReceived}</td>
        <td className="px-3 py-3 text-secondary">
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </td>
      </tr>
      {isOpen && rowData.appWiseUsageData && (
        <tr>
          <td colSpan="8" className="p-3 ">
            <div className="accordion-wrapper noScroll border rounded" style={{ 
              maxHeight: '300px', 
              overflowY: 'auto',
              msOverflowStyle: 'none',  // IE and Edge
              scrollbarWidth: 'none',   // Firefox
            }}>
              <div className="accordion" id={`appAccordion-${rowData.date}`}>
                {rowData.appWiseUsageData.map((app, idx) => (
                  <div key={idx} className="accordion-item border-0">
                    <div 
                      className="row accordion-header d-flex justify-content-between align-items-center p-2 border-bottom bg-light cursor-pointer"
                      onClick={() => setSelectedApp(selectedApp === idx ? null : idx)}
                    >
                      <div className="col-12 col-md-4 d-flex align-items-center gap-2">
                      {getStatusIcon(app.isAuthorised)}
                        <Smartphone size={14} className="text-secondary" />
                        <span className="fw-medium small">{app.appName}</span>
                      </div>
                     
                      <div className="col-12 col-md-3 d-flex align-items-center gap-2">
                        <span className="text-secondary small">Launch Count :</span>
                        <span className="text-secondary small fw-medium">
                          {app.launchCount}
                        </span>
                      </div>

                      <div className="col-12 col-md-3 d-flex align-items-center gap-2">
                        <span className="text-secondary small "> App Usage Time :</span>
                        <span className="text-secondary small fw-medium">
                          {app.usageTime}
                        </span>
                       
                      </div>

                      <div className="col-12 col-md-1 d-flex align-items-center gap-2 ">
                        <Bell size={14} className="text-secondary" />
                        <span className="fw-medium ">{app.notificationCount}</span>
                      </div>

                      <div className="col-12 col-md-1 d-flex align-items-center gap-2 ">
                        {selectedApp === idx ? <ChevronUp size={14} /> : <ChevronDown size={16} />}
                      </div>
                    </div>
                    
                    {selectedApp === idx && (
                      <div className="accordion-body p-2 bg-white">
                        <div className="row">
                          <div className="col-12">
                            <h6 className="text-secondary mb-2 ms-2">Usage Intervals</h6>
                            <div className="usage-intervals-container px-2">
                              <div className="d-flex flex-wrap gap-2">
                                {app.usageIntervals?.map((interval, i) => {
                                  const startTime = formatTimestamp(interval.startTime);
                                  const endTime = formatTimestamp(interval.endTime);
                                  return (
                                    <div key={i} className="usage-interval-badge">
                                      <small className="text-secondary">
                                        {startTime} → {endTime}
                                      </small>
                                    </div>
                                  );
                                }) || (
                                  <div className="text-center text-muted">
                                    <small>No usage data available</small>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

function ApplicationUsageReport () {
  // Suspension check
  const { isSuspended } = useSuspension();

  const globalLocationFilter = useSelector((state) => state.selectedLocationFilter);
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  const superAdminSelectedOrgData = useSelector((state) => state.superAdminSelectedOrgData);
  const superAdminSelectedSubOrgId = superAdminSelectedOrgData?.type === 'sub_org' ? superAdminSelectedOrgData?.sub_org_id : null;
  const [isReportJustGenerated, setIsReportJustGenerated] = useState(false);
  const [isDownloadEnabled, setIsDownloadEnabled] = useState(false);
  const [hasGeneratedReport, setHasGeneratedReport] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null); // State to track selected member
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [selectedUser, setSelectedUser] = useState(null); // Replace selectedUsers state with this
  const [users, setUsers] = useState([]); // Add this state for users list
  const [isFirstAccordionOpen, setIsFirstAccordionOpen] = useState(true);
  const [isSecondAccordionOpen, setIsSecondAccordionOpen] = useState(true); // Changed to true
  const [subAccordions, setSubAccordions] = useState({
    first: true,
    second: true,
    third: true,
    fourth: true,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [reportData, setReportData] = useState(null);
  const [includeData, setIncludeData] = useState(false);

  const recordsPerPage = 20;
  const totalPages = reportData?.summaryData?.daily
    ? Math.ceil(reportData.summaryData.daily.length / recordsPerPage)
    : 0;

  const currentTableData = reportData?.summaryData?.daily
    ? reportData.summaryData.daily.slice(
        (currentPage - 1) * recordsPerPage,
        currentPage * recordsPerPage
      )
    : [];

  console.log("currentTableData", currentTableData);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetchOrganizationUsers(globalLocationFilter, superAdminSelectedOrgId, superAdminSelectedSubOrgId);
        if (response?.businessStatusCode === 2) {
          const userOptions =
            response.data?.map((user) => ({
              name: user.full_name,
              code: user.user_id,
            })) || [];
          console.log("userOptions", userOptions);
          setUsers(userOptions);
          // Reset selected user when location filter changes
          setSelectedUser(null);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [globalLocationFilter, superAdminSelectedOrgId, superAdminSelectedSubOrgId]);

  const handleGenerateReport = async () => {
    if (!selectedUser) {
      toast.error("Please select a user");
      return;
    }

    const payload = {
      fromDate: fromDate.toISOString().split("T")[0],
      toDate: toDate.toISOString().split("T")[0],
      userId: selectedUser.code,
      individualAppData: includeData,
    };
    // Super Admin: scope the report to the org selected in the top-bar filter.
    if (superAdminSelectedOrgId) payload.org_id = superAdminSelectedOrgId;
    if (superAdminSelectedSubOrgId) payload.sub_org_id = superAdminSelectedSubOrgId;

    try {
      const response = await generateApplicationUsageReport(payload);

      if (response?.businessStatusCode === 2) {
        toast.success("Report generated successfully!");
        // Set the entire response data
        console.log("response", response);
        setReportData(response);
        console.log("reportData", reportData);
        setIsReportJustGenerated(true);
        setIsDownloadEnabled(true); 
        setHasGeneratedReport(true); 
      }
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("Failed to generate report");
    }
  };

  useEffect(() => {
    if (isReportJustGenerated) {
      setIsReportJustGenerated(false);
      // Only disable download if we haven't generated a report before
      if (!hasGeneratedReport) {
        setIsDownloadEnabled(false);
      }
    }
  }, [fromDate, toDate, selectedUser]);

  const isFormValid = () => {
    return fromDate && toDate && selectedUser && !isReportJustGenerated;
  };

  const handleDownloadPDF = () => {
    if (!reportData) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text("Application Usage Report", pageWidth / 2, 20, { align: "center" });
    
    // User and Date Info
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text(`User: ${selectedUser.name}`, 14, 35);
    doc.text(`Period: ${fromDate.toLocaleDateString("en-GB")} - ${toDate.toLocaleDateString("en-GB")}`, 14, 42);
    doc.text(`Generated: ${new Date().toLocaleDateString("en-GB")} ${new Date().toLocaleTimeString()}`, 14, 49);
    
    // Summary Section
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    doc.text("Summary", 14, 62);
    
    const summaryData = [
      ["Total Screen Time", reportData.summaryData?.totalScreenTime || "0h 0m"],
      ["Total Authorised App Usage Time", reportData.summaryData?.totalAuthorisedAppUsageTime || "0h 0m"],
      ["Total Unauthorised App Usage Time", reportData.summaryData?.totalUnauthorisedAppUsageTime || "0h 0m"],
      ["Total Uncategorised App Usage Time", reportData.summaryData?.totalUncategorisedAppUsageTime || "0h 0m"],
      ["Total Screen Unlocks", reportData.summaryData?.totalScreenUnlocks || "0"],
      ["Total Notifications Received", reportData.summaryData?.totalNotificationsReceived || "0"],
    ];

    autoTable(doc, {
      startY: 68,
      head: [["Metric", "Value"]],
      body: summaryData,
      theme: "grid",
      headStyles: {
        fillColor: [79, 129, 189],
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: "bold",
        halign: "center",
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [60, 60, 60],
      },
      columnStyles: {
        0: { cellWidth: 120, fontStyle: "bold" },
        1: { cellWidth: 60, halign: "center" },
      },
      alternateRowStyles: {
        fillColor: [249, 249, 249],
      },
      margin: { left: 14, right: 14 },
    });

    // Daily Usage Section
    doc.addPage();
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    doc.text("Daily Usage Report", 14, 20);

    const dailyData = reportData.usageData?.map((row) => [
      new Date(row.date).toLocaleDateString("en-GB"),
      row.totalScreenTime || "0h 0m",
      row.authorisedAppUsageTime || "0h 0m",
      row.unauthorisedAppUsageTime || "0h 0m",
      row.uncategorisedAppUsageTime || "0h 0m",
      row.screenUnlocks || "0",
      row.notificationsReceived || "0",
    ]) || [];

    autoTable(doc, {
      startY: 28,
      head: [["Date", "Screen Time", "Authorised", "Unauthorised", "Uncategorised", "Unlocks", "Notifications"]],
      body: dailyData,
      theme: "grid",
      headStyles: {
        fillColor: [79, 129, 189],
        textColor: [255, 255, 255],
        fontSize: 7,
        fontStyle: "bold",
        halign: "center",
      },
      bodyStyles: {
        fontSize: 7,
        textColor: [60, 60, 60],
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 25 },
        2: { cellWidth: 25 },
        3: { cellWidth: 28 },
        4: { cellWidth: 28 },
        5: { cellWidth: 20 },
        6: { cellWidth: 28 },
      },
      alternateRowStyles: {
        fillColor: [249, 249, 249],
      },
      margin: { left: 14, right: 14 },
    });

    // App Usage Details (if included)
    if (includeData && reportData.usageData && reportData.usageData.length > 0) {
      doc.addPage();
      doc.setFontSize(11);
      doc.setTextColor(40, 40, 40);
      doc.text("Application Usage Details", 14, 20);

      reportData.usageData.forEach((dayData, index) => {
        if (index > 0) doc.addPage();
        
        doc.setFontSize(10);
        doc.text(`Date: ${new Date(dayData.date).toLocaleDateString("en-GB")}`, 14, 28);
        
        const appData = dayData.appWiseUsageData?.map((app) => [
          app.appName || "Unknown",
          app.packageName || "N/A",
          app.usageTime || "0h 0m",
          app.launchCount || "0",
          app.isAuthorised || "Uncategorised",
        ]) || [];

        autoTable(doc, {
          startY: 35,
          head: [["App Name", "Package", "Usage Time", "Count", "Status"]],
          body: appData,
          theme: "grid",
          headStyles: {
            fillColor: [79, 129, 189],
            textColor: [255, 255, 255],
            fontSize: 7,
            fontStyle: "bold",
            halign: "center",
          },
          bodyStyles: {
            fontSize: 7,
            textColor: [60, 60, 60],
          },
          columnStyles: {
            0: { cellWidth: 45 },
            1: { cellWidth: 50 },
            2: { cellWidth: 30, halign: "center" },
            3: { cellWidth: 20, halign: "center" },
            4: { cellWidth: 35, halign: "center" },
          },
          alternateRowStyles: {
            fillColor: [249, 249, 249],
          },
          margin: { left: 14, right: 14 },
        });
      });
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }

    const userName = selectedUser.name.replace(/\s+/g, "_");
    const fileName = `${userName}_AppUsage_${fromDate.toISOString().split("T")[0]}_to_${toDate.toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);
  };

  const handleApplicationDataChange = (e) => {
    setIncludeData(e.target.checked);
  };
  return (
    <div className="row bg-white rounded shadow pt-3 px-3">
      <div className="col-12 col-md-12 d-flex justify-content-between p-0">
        <h1 className="fw-bold">Application Usage Report</h1>
        <ArthaCancelButton
          className="px-2"
          onClick={isSuspended ? undefined : handleDownloadPDF}
          disabled={!isDownloadEnabled || isSuspended}
          title={isSuspended ? "Download disabled - Organization is suspended" : ""}
          style={{
            opacity: (isDownloadEnabled && !isSuspended) ? 1 : 0.5,
            cursor: (isDownloadEnabled && !isSuspended) ? "pointer" : "not-allowed",
          }}
        >
          <Download size={13} /> &nbsp; Download PDF
        </ArthaCancelButton>
      </div>

      <div className="row p-0 bg-white my-2 pb-1 border rounded d-flex m-0">
        {/* From Date Picker */}
        <div className="col-12 col-md-2 mb-2 pt-2">
          <label className="form-label">From Date</label>
          <DatePicker
            selected={fromDate}
            onChange={(date) => setFromDate(date)}
            onChangeRaw={handleDatePickerRawChange("dd/MM/yyyy")}
            className="form-control"
            dateFormat="dd/MM/yyyy"
            maxDate={toDate}
            maxLength={dateFormatMaxLength("dd/MM/yyyy")}
            withPortal
          />
        </div>

        {/* To Date Picker */}
        <div className="col-12 col-md-2 mb-2 pt-2">
          <label className="form-label">To Date</label>
          <DatePicker
            selected={toDate}
            onChange={(date) => setToDate(date)}
            onChangeRaw={handleDatePickerRawChange("dd/MM/yyyy")}
            className="form-control"
            dateFormat="dd/MM/yyyy"
            maxDate={new Date()}
            minDate={fromDate}
            maxLength={dateFormatMaxLength("dd/MM/yyyy")}
            withPortal
          />
        </div>

        {/* User Selection Dropdown */}
        <div className="col-12 col-md-5 mb-2 pt-2">
          <label className="form-label">Select User</label>
          <Select
            options={users}
            value={selectedUser}
            onChange={setSelectedUser}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.code}
            placeholder="Select a user..."
            className="w-full border"
            styles={{
              control: (base) => ({
                ...base,
                height: "32px",
                minHeight: "32px",
                fontSize: "12px",
              }),
              dropdownIndicator: (base) => ({
                ...base,
                padding: "6px",
              }),
              clearIndicator: (base) => ({
                ...base,
                padding: "6px",
              }),
              input: (base) => ({
                ...base,
                margin: "0px",
                padding: "0px",
              }),
              valueContainer: (base) => ({
                ...base,
                padding: "0 6px",
              }),
            }}
          />
        </div>

        {/* Generate Report Button */}
        <div className="col-12 col-md-3 mb-2 d-flex align-items-end">
          <ArthaContinueButton
            onClick={isSuspended ? undefined : handleGenerateReport}
            className="shadow-sm w-100"
            disabled={!isFormValid() || isSuspended}
            title={isSuspended ? "Action disabled - Organization is suspended" : ""}
            style={{
              opacity: (isFormValid() && !isSuspended) ? 1 : 0.5,
              cursor: (isFormValid() && !isSuspended) ? "pointer" : "not-allowed",
            }}
          >
            Generate Report
          </ArthaContinueButton>
        </div>

        <div className="col-12 col-md-12 d-flex">
        <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeData}
                      onChange={handleApplicationDataChange}
                      sx={{
                        color: orange[800],
                        '&.Mui-checked': {
                          color: orange[600],
                        },
                        '& .MuiSvgIcon-root': { // Add this style block to control size
                          fontSize: 28, // Increase icon size (default is 24)
                          width: '1.5em', // Increase width
                          height: '1.5em', // Increase height
                        },
                      
                      }}
                    />
                  }
                  label="Include individual application data"
                />
        </div>
      </div>

      {reportData ? (
        <div>
          {/* Second Accordion - Table */}
          <div className="row mt-2 mb-3 bg-white rounded border ">
            <div
              className="px-3 py-2 bg-gray-100 border-b cursor-pointer flex justify-between items-center rounded"
              onClick={() => setIsFirstAccordionOpen(!isFirstAccordionOpen)}
            >
              <h5 className="m-0 font-medium">Application Usage Report</h5>
              {isFirstAccordionOpen ? (
                <ChevronUp size={20} />
              ) : (
                <ChevronDown size={20} />
              )}
            </div>

            {isFirstAccordionOpen && (
              <div className="px-3">
                <div className="row overflow-x-auto">
                  <table className="min-w-full table-auto border-collapse">
                    <thead className="bg-gray-100">
                      <tr className="border-b-2 border-gray-300">
                        <th className="col-1 px-3 py-2 font-medium text-gray-700">Date</th>
                        <th className="col-2 px-3 py-2 font-medium text-gray-700">Screen Time</th>
                        <th className="col-2 px-3 py-2 font-medium text-gray-700">Authorised App Usage </th>
                        <th className="col-2 px-3 py-2 font-medium text-gray-700">Unauthorised App Usage </th>                       
                        <th className="col-2 px-3 py-2 font-medium text-gray-700">Uncategorised App Usage </th>                       
                        <th className="col-1 px-3 py-2 font-medium text-gray-700">Screen Unlocks</th>
                        <th className="col-1 px-3 py-2 font-medium text-gray-700">Notifications</th>
                        <th className="col-1 px-3 py-2 font-medium text-gray-700"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {reportData?.usageData?.map((row, index) => (
                        <AppUsageRow key={index} rowData={row} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="col-12 col-md-12 mx-auto px-5 py-3 mt-4">
          <UserImage
            alt="Image Not Found"
            className="w-50 h-50 mx-auto px-5 py-2"
          />
          <p className="fw-bold text-center mt-3">
            Kindly generate the report to view it here.
          </p>
        </div>
      )}
    </div>
  );
}

export default ApplicationUsageReport;
